import {
  addToaster,
  getGenericEntityListingAPI,
  GetGenericEntityListingApiType,
  hideLoader,
  showLoader,
  WorkflowInstanceTaskType,
} from '@app-store'
import {
  Accordion,
  CustomButton,
  CustomRadioGroup,
  DATE_FIELD_TYPES,
  FormInput,
  ViewComponent,
} from '@ui-kit'
import {
  ENTITY_IMPORTANT_KEYS,
  NAMESPACES,
  PHARMACY_TRIAGE_CONSTANTS,
  WORKFLOW_STATUS,
} from '../../utils/constants'
import { PHARMACY_TRIAGE_STRINGS } from '../../utils/strings'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { editEntityData } from '../../apis/entityData.api'
import { useAppDispatch } from '../../utils/agent.store'
import { TOASTER_NAMESPACE } from '../../constants/addEditPage.strings'
import { v4 as uuid } from 'uuid'
import moment from 'moment'

interface FormValues {
  queue_date: string | null
  new_queue_date: string | null
}

export const PharmacyTriageOrderDetails = ({
  task,
}: {
  task: WorkflowInstanceTaskType
}) => {
  const [selectedOption, setSelectedOption] = useState<{
    queue_date: boolean
    new_queue_date: boolean
  }>({
    queue_date: true,
    new_queue_date: false,
  })

  const {
    setValue,
    register,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: { queue_date: null, new_queue_date: null },
  })

  const dispatch = useAppDispatch()
  const { caseId } = useParams()

  const [recordId, setRecordId] = useState(null)
  const onSubmit = async (data: FormValues) => {
    try {
      dispatch(showLoader())
      const response = await editEntityData(
        task?.identifier?.entity_name ?? '',
        Number(task?.identifier?.entity_id),
        0,
        {
          queue_date: data.new_queue_date,
          id: recordId,
        },
        recordId ?? ''
      )
      if (response) {
        dispatch(
          addToaster({
            id: uuid(),
            type: 'success',
            message: TOASTER_NAMESPACE.SAVE_SUCCESS,
          })
        )
        fetchQeuedDate()
        setSelectedOption({
          queue_date: true,
          new_queue_date: false,
        })
        setValue('new_queue_date', null)
      }
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const fetchQeuedDate = async () => {
    try {
      const payload = {
        entity_id: '2463',
        select: [
          ENTITY_IMPORTANT_KEYS.ID,
          PHARMACY_TRIAGE_CONSTANTS.QUEUE_DATE,
        ],
        distinct: 'true',
        page: 0,
        size: 25,
        filters: [
          {
            criteria: [
              {
                field_name: 'cases_id',
                operation: 'EQUALS',
                values: [caseId],
              },
            ],
          },
        ],
      }
      const response = await getGenericEntityListingAPI(
        payload as unknown as GetGenericEntityListingApiType
      )
      if (response) {
        setRecordId(response?.content?.[0]?.id)
        setValue('queue_date', response?.content?.[0]?.queue_date)
      }
    } catch (err) {
      console.error(err)
    }
  }

  const getMinDate = () => {
    if (task.start_date) {
      return moment(task.start_date).format('YYYY-MM-DDTHH:mm')
    }
    return ''
  }

  useEffect(() => {
    if (!getValues('queue_date')) {
      fetchQeuedDate()
    }
  }, [])

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="border rounded-b border-accent-100 flex flex-col">
        <Accordion
          heading={
            <div className="flex justify-between">
              <span className="flex items-center">
                {PHARMACY_TRIAGE_STRINGS.CALCULATE_QUEUED_DATE}
              </span>
              {task.identifier?.action_buttons?.submit_manual_override &&
                task.state === WORKFLOW_STATUS.OPEN && (
                  <CustomButton
                    type="submit"
                    onClick={(e) => {
                      e.stopPropagation()
                    }}
                    verticalPaddingClasses="py-0.5"
                    horizontalPaddingClasses="px-3"
                    disabled={selectedOption.queue_date}
                    widthClass={'rounded-lg'}
                    isFilled={true}
                  >
                    <span>{NAMESPACES.SUBMIT_MANUAL_OVERRIDE}</span>
                  </CustomButton>
                )}
            </div>
          }
          alwaysOpen={true}
        >
          {task.state === WORKFLOW_STATUS.OPEN ? (
            <div className="p-2 flex w-full">
              <div className="w-full flex px-2 py-4">
                <CustomRadioGroup
                  options={[{ label: '', value: '' }]}
                  name={'queue_date_radio'}
                  optionClasses="flex items-center justify-center"
                  checked={selectedOption.queue_date}
                  onChange={() => {
                    setSelectedOption({
                      queue_date: true,
                      new_queue_date: false,
                    })
                  }}
                />
                <div className="w-full">
                  <FormInput
                    label={PHARMACY_TRIAGE_CONSTANTS.QUEUE_DATE_LABEL}
                    type={DATE_FIELD_TYPES.DATE_TIME_LOCAL}
                    name={'queue_date'}
                    disabled={true}
                    register={register}
                  />
                </div>
              </div>
              <div className="w-full flex px-2 py-4">
                <CustomRadioGroup
                  options={[{ label: '', value: '' }]}
                  name={'new_queue_date_radio'}
                  optionClasses="flex items-center justify-center"
                  checked={selectedOption.new_queue_date}
                  onChange={() => {
                    setSelectedOption({
                      queue_date: false,
                      new_queue_date: true,
                    })
                  }}
                />
                <div className="w-full">
                  <FormInput
                    label={PHARMACY_TRIAGE_CONSTANTS.NEW_QUEUE_DATE_LABEL}
                    type={DATE_FIELD_TYPES.DATE_TIME_LOCAL}
                    name={'new_queue_date'}
                    onChange={(e) =>
                      setValue('new_queue_date', e.target.value.toString())
                    }
                    min={getMinDate()}
                    validation={{
                      required: selectedOption.new_queue_date,
                    }}
                    errors={errors}
                    register={register}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 w-full">
              <ViewComponent
                label={'Queued Date'}
                data={
                  getValues('queue_date')
                    ? moment(getValues('queue_date')).format('MM/DD/YYYY HH:mm')
                    : '--'
                }
              />
            </div>
          )}
        </Accordion>
      </div>
    </form>
  )
}
